/**
 * The `?` marker: a definition on hover, the full explanation on click.
 *
 * Two constraints shaped this. It has to be reachable by keyboard, so it is a
 * real `<button>` and the bubble opens on focus as well as hover. And it has to
 * escape `.panel { overflow-y: auto }`, which would otherwise clip a bubble
 * near the edge of a scrolling column — hence `position: fixed` with
 * coordinates taken from the button, rather than an absolutely positioned child.
 *
 * Position is computed in the event handler from the button's own rectangle
 * rather than in a layout effect after measuring the bubble. That costs a
 * little placement finesse and buys correctness under server rendering, where
 * layout effects do not run at all.
 */

import { useCallback, useId, useState } from 'react';
import { TOPICS, type TopicId } from './glossary.tsx';
import { useGuide } from './guide-context.ts';

/** Half the bubble's max width, plus a margin — how close it may get to an edge. */
const EDGE = 176;
/** Enough room below the marker to place the bubble there rather than above. */
const BELOW_CLEARANCE = 220;

interface Placement {
  x: number;
  y: number;
  above: boolean;
}

export function Info({ topic }: { topic: TopicId }) {
  const t = TOPICS[topic];
  const guide = useGuide();
  const bubbleId = useId();
  const [placement, setPlacement] = useState<Placement | null>(null);

  const show = useCallback((el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const above = window.innerHeight - r.bottom < BELOW_CLEARANCE;
    setPlacement({
      x: Math.min(Math.max(r.left + r.width / 2, EDGE), Math.max(EDGE, window.innerWidth - EDGE)),
      y: above ? r.top - 8 : r.bottom + 8,
      above,
    });
  }, []);

  const hide = useCallback(() => setPlacement(null), []);

  return (
    <span className="info">
      <button
        type="button"
        className="info-btn"
        // Inside a <label>, a bare click would also toggle the label's control.
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          guide.open(topic);
        }}
        onPointerEnter={(e) => show(e.currentTarget)}
        onPointerLeave={hide}
        onFocus={(e) => show(e.currentTarget)}
        onBlur={hide}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && placement) {
            e.stopPropagation();
            hide();
          }
        }}
        aria-label={`What is ${t.term}? Opens the guide.`}
        aria-describedby={placement ? bubbleId : undefined}
      >
        ?
      </button>

      {placement && (
        <span
          id={bubbleId}
          role="tooltip"
          className={placement.above ? 'info-bubble above' : 'info-bubble'}
          style={{ left: `${placement.x}px`, top: `${placement.y}px` }}
        >
          <strong>{t.term}</strong>
          <span>{t.short}</span>
          <em>Click to read more</em>
        </span>
      )}
    </span>
  );
}
